/**
 * Saisie de notes d'examen PAR CODE DE COPIE.
 *
 * Workflow :
 *   1. L'utilisateur tape le code de la copie (ex. 123, 045/L1MI/Math, …)
 *   2. Le système recherche la copie via /copies-examens/rechercher_par_code/
 *      → retrouve étudiant + matière associés
 *   3. La fiche s'affiche : matricule, nom, classe, matière (verrouillée par le code)
 *   4. L'opérateur saisit la note (ou coche AB) → Entrée pour sauvegarder
 *   5. Focus auto retourne au champ "code" pour enchaîner avec la copie suivante
 *   6. Liste vivante des saisies récentes (avec rollback visuel en cas d'erreur)
 *
 * Le code copie est la SOURCE DE VÉRITÉ : la matière utilisée pour enregistrer
 * la note est celle liée à la copie (via association), pas celle pré-sélectionnée
 * en paramètre. Si l'opérateur tape une copie d'une autre matière, c'est cette
 * autre matière qui sera notée — un avertissement visuel l'indique clairement.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Search, Save, Loader, CheckCircle2, XCircle, AlertTriangle,
  User, BookOpen, Hash, Trash2,
} from 'lucide-react';
import { useLazyRechercherCopieParCodeQuery } from '../../services/copiesApi';
import { useSaisieIndividuelleMutation } from '../../services/notesExamensApi';

interface Props {
  matiereId: number;
  anneeAcademique: string;
  semestre: 'S1' | 'S2';
  classe: string;
  estRattrapage: boolean;
}

interface SaisieRecente {
  id: string;
  code_copie: string;
  matricule: string;
  etudiant_nom: string;
  classe: string;
  matiere_code: string;
  matiere_nom: string;
  matiere_id: number;
  note: number | null;
  absent: boolean;
  status: 'pending' | 'success' | 'error';
  message?: string;
  hors_scope: boolean;  // matière de la copie ≠ matière pré-sélectionnée
  timestamp: number;
}

const SaisieParCodeCopieExamens: React.FC<Props> = ({
  matiereId, anneeAcademique, semestre, classe, estRattrapage,
}) => {
  const [code, setCode] = useState('');
  const [foundCopie, setFoundCopie] = useState<any | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [absent, setAbsent] = useState(false);
  const [recentes, setRecentes] = useState<SaisieRecente[]>([]);
  const [globalMsg, setGlobalMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const codeInputRef = useRef<HTMLInputElement>(null);
  const noteInputRef = useRef<HTMLInputElement>(null);

  const [rechercher, { isFetching: searching }] = useLazyRechercherCopieParCodeQuery();
  const [saisirNote, { isLoading: saving }] = useSaisieIndividuelleMutation();

  // Focus initial sur le champ code
  useEffect(() => {
    codeInputRef.current?.focus();
  }, []);

  // Quand une copie est trouvée, focus auto sur le champ note
  useEffect(() => {
    if (foundCopie) {
      setTimeout(() => noteInputRef.current?.focus(), 50);
    }
  }, [foundCopie]);

  const resetForCode = () => {
    setCode('');
    setFoundCopie(null);
    setSearchError(null);
    setNote('');
    setAbsent(false);
    setTimeout(() => codeInputRef.current?.focus(), 50);
  };

  const handleSearch = useCallback(async () => {
    const c = code.trim();
    if (!c) return;
    setSearchError(null);
    setFoundCopie(null);
    try {
      const result = await rechercher(c).unwrap();
      if (!result || !result.id) {
        setSearchError(`Aucune copie trouvée pour le code "${c}"`);
        return;
      }
      if (!result.etudiant) {
        setSearchError(`Copie ${c} trouvée mais non associée à un étudiant`);
        return;
      }
      setFoundCopie(result);
    } catch (e: any) {
      setSearchError(e?.data?.error || e?.data?.detail || `Code "${c}" introuvable`);
    }
  }, [code, rechercher]);

  // Recherche automatique sur Entrée dans le champ code
  const handleCodeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSauvegarder = useCallback(async () => {
    if (!foundCopie || !foundCopie.etudiant) return;
    const n = note.trim() === '' ? null : parseFloat(note);
    if (n === null && !absent) {
      setGlobalMsg({ type: 'error', text: 'Saisis une note ou coche AB' });
      setTimeout(() => setGlobalMsg(null), 2000);
      return;
    }
    if (n !== null && (isNaN(n) || n < 0 || n > 20)) {
      setGlobalMsg({ type: 'error', text: `Note invalide : ${note} (doit être entre 0 et 20)` });
      setTimeout(() => setGlobalMsg(null), 3000);
      return;
    }

    const matiere_id_cible = foundCopie.matiere;
    const horsScope = matiere_id_cible !== matiereId;

    const localId = `${foundCopie.id}_${Date.now()}`;
    const entry: SaisieRecente = {
      id: localId,
      code_copie: foundCopie.code_copie,
      matricule: foundCopie.etudiant_matricule,
      etudiant_nom: foundCopie.etudiant_nom,
      classe: foundCopie.etudiant_classe || '',
      matiere_code: foundCopie.matiere_code,
      matiere_nom: foundCopie.matiere_nom,
      matiere_id: matiere_id_cible,
      note: absent ? null : n,
      absent,
      status: 'pending',
      hors_scope: horsScope,
      timestamp: Date.now(),
    };
    setRecentes(prev => [entry, ...prev].slice(0, 50));

    try {
      await saisirNote({
        etudiant_id: foundCopie.etudiant,
        matiere_id: matiere_id_cible,
        annee_academique: anneeAcademique,
        semestre,
        est_rattrapage: estRattrapage,
        note_examen: absent ? null : n,
        absent,
      }).unwrap();
      setRecentes(prev => prev.map(r =>
        r.id === localId ? { ...r, status: 'success', message: 'Sauvegardée' } : r
      ));
      setGlobalMsg({
        type: 'success',
        text: `${foundCopie.code_copie} (${foundCopie.etudiant_matricule}) — sauvegardée ✓`,
      });
      setTimeout(() => setGlobalMsg(null), 1500);
      resetForCode();
    } catch (e: any) {
      const msg = e?.data?.error || e?.data?.detail || e?.message || 'Erreur lors de la sauvegarde';
      setRecentes(prev => prev.map(r =>
        r.id === localId ? { ...r, status: 'error', message: msg } : r
      ));
      setGlobalMsg({ type: 'error', text: msg });
    }
  }, [foundCopie, note, absent, matiereId, anneeAcademique, semestre, estRattrapage, saisirNote]);

  const handleNoteKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSauvegarder();
    }
  };

  const horsScope = foundCopie && foundCopie.matiere !== matiereId;
  const sessionLabel = estRattrapage ? 'Session de rattrapage (SR)' : 'Session ordinaire (SO)';

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      {/* Bandeau contexte */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 text-xs flex items-center gap-3 flex-wrap">
        <Hash className="text-blue-600 shrink-0" size={18} />
        <div className="flex-1">
          <div className="font-semibold text-blue-900 dark:text-blue-100 text-sm">
            Saisie par code de copie
          </div>
          <div className="text-blue-700 dark:text-blue-300">
            Tape le code de la copie → l'étudiant et la matière sont retrouvés automatiquement.
            Saisis la note → Entrée pour sauvegarder et passer à la suivante.
          </div>
        </div>
        <div className="text-right text-blue-700 dark:text-blue-300">
          <div>{anneeAcademique} · {semestre}</div>
          <div>{sessionLabel}</div>
        </div>
      </div>

      {/* Bloc de saisie */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-3">
        {/* Champ code */}
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Code de la copie
          </label>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Hash size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                ref={codeInputRef}
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={handleCodeKeyDown}
                placeholder="Entre le code et appuie sur Entrée…"
                disabled={!!foundCopie}
                className="w-full pl-9 pr-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 disabled:bg-gray-100 dark:disabled:bg-gray-900"
              />
              {searching && (
                <Loader size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />
              )}
            </div>
            {foundCopie ? (
              <button
                onClick={resetForCode}
                className="flex items-center gap-1 px-3 py-2 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200"
                title="Réinitialiser pour saisir un nouveau code"
              >
                <XCircle size={14} /> Annuler
              </button>
            ) : (
              <button
                onClick={handleSearch}
                disabled={!code.trim() || searching}
                className="flex items-center gap-1 px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50"
              >
                {searching ? <Loader size={14} className="animate-spin" /> : <Search size={14} />}
                Rechercher
              </button>
            )}
          </div>
          {searchError && (
            <div className="mt-2 p-2 text-xs bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 rounded flex items-start gap-2">
              <XCircle size={14} className="shrink-0 mt-0.5" />
              <span>{searchError}</span>
            </div>
          )}
        </div>

        {/* Fiche copie trouvée */}
        {foundCopie && (
          <>
            <div className={`p-3 rounded-lg border ${
              horsScope
                ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700'
                : 'bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-700'
            }`}>
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-full ${
                  horsScope
                    ? 'bg-amber-100 dark:bg-amber-800/40 text-amber-700 dark:text-amber-300'
                    : 'bg-green-100 dark:bg-green-800/40 text-green-700 dark:text-green-300'
                }`}>
                  <User size={18} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-bold text-base text-gray-900 dark:text-gray-100">
                      {foundCopie.etudiant_matricule}
                    </span>
                    <span className="text-base text-gray-700 dark:text-gray-300">
                      {foundCopie.etudiant_nom}
                    </span>
                    {foundCopie.etudiant_classe && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded font-semibold">
                        {foundCopie.etudiant_classe}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
                    <BookOpen size={12} />
                    Matière : <span className="font-semibold">{foundCopie.matiere_code}</span> — {foundCopie.matiere_nom}
                  </div>
                  {foundCopie.est_notee && (
                    <div className="mt-1 text-[11px] text-orange-700 dark:text-orange-300 flex items-center gap-1">
                      <AlertTriangle size={11} /> Cette copie est déjà marquée comme notée. La nouvelle note remplacera l'ancienne.
                    </div>
                  )}
                  {horsScope && (
                    <div className="mt-2 text-[11px] bg-amber-100 dark:bg-amber-800/40 border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 rounded p-2 flex items-start gap-1.5">
                      <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                      <span>
                        <strong>Attention :</strong> la copie est rattachée à une matière
                        différente de celle pré-sélectionnée. La note sera enregistrée pour
                        la matière de la copie : <strong>{foundCopie.matiere_code}</strong>.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Saisie note */}
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Note (sur 20)
                </label>
                <input
                  ref={noteInputRef}
                  type="number"
                  min="0" max="20" step="0.25"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  onKeyDown={handleNoteKeyDown}
                  disabled={absent}
                  placeholder="Ex: 14.5"
                  className="w-full px-3 py-2 text-base font-bold text-center border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100 dark:disabled:bg-gray-900"
                />
              </div>
              <label className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300 cursor-pointer pb-2">
                <input
                  type="checkbox"
                  checked={absent}
                  onChange={(e) => {
                    setAbsent(e.target.checked);
                    if (e.target.checked) setNote('');
                  }}
                  className="rounded"
                />
                Absent (AB)
              </label>
              <button
                onClick={handleSauvegarder}
                disabled={saving || (!note.trim() && !absent)}
                title="Entrée pour sauvegarder"
                className="flex items-center gap-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-semibold disabled:opacity-50"
              >
                {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
                Sauvegarder
              </button>
            </div>
          </>
        )}
      </div>

      {/* Message global */}
      {globalMsg && (
        <div className={`p-2 rounded text-sm flex items-center gap-2 ${
          globalMsg.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700'
            : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700'
        }`}>
          {globalMsg.type === 'success' ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
          {globalMsg.text}
        </div>
      )}

      {/* Liste des saisies récentes */}
      {recentes.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-4 py-2 border-b dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Saisies récentes ({recentes.length})
            </h3>
            <button
              onClick={() => setRecentes([])}
              className="text-xs text-gray-500 hover:text-red-600 flex items-center gap-1"
            >
              <Trash2 size={11} /> Effacer la liste
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-600 dark:text-gray-400">
                <tr>
                  <th className="text-left px-3 py-1.5 font-medium">Heure</th>
                  <th className="text-left px-3 py-1.5 font-medium">Code</th>
                  <th className="text-left px-3 py-1.5 font-medium">Matricule</th>
                  <th className="text-left px-3 py-1.5 font-medium">Nom</th>
                  <th className="text-left px-3 py-1.5 font-medium">Matière</th>
                  <th className="text-center px-3 py-1.5 font-medium">Note</th>
                  <th className="text-left px-3 py-1.5 font-medium">Statut</th>
                </tr>
              </thead>
              <tbody>
                {recentes.map(r => (
                  <tr key={r.id} className={`border-t border-gray-100 dark:border-gray-700 ${
                    r.hors_scope ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''
                  }`}>
                    <td className="px-3 py-1.5 text-gray-500 font-mono">
                      {new Date(r.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </td>
                    <td className="px-3 py-1.5 font-mono font-semibold">{r.code_copie}</td>
                    <td className="px-3 py-1.5 font-mono">{r.matricule}</td>
                    <td className="px-3 py-1.5">{r.etudiant_nom}</td>
                    <td className="px-3 py-1.5">
                      <span className="font-semibold">{r.matiere_code}</span>
                      {r.hors_scope && (
                        <span className="ml-1 text-[10px] text-amber-700 dark:text-amber-300" title="Matière différente de celle pré-sélectionnée">
                          ⚠
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-center font-bold">
                      {r.absent ? <span className="text-orange-600">AB</span> : (r.note != null ? r.note.toFixed(2) : '—')}
                    </td>
                    <td className="px-3 py-1.5">
                      {r.status === 'pending' && <span className="text-gray-500 flex items-center gap-1"><Loader size={11} className="animate-spin" /> En cours…</span>}
                      {r.status === 'success' && <span className="text-green-600 flex items-center gap-1"><CheckCircle2 size={11} /> {r.message}</span>}
                      {r.status === 'error' && <span className="text-red-600 flex items-center gap-1" title={r.message}><XCircle size={11} /> Erreur</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Astuce clavier */}
      <div className="text-[11px] text-gray-500 dark:text-gray-400 text-center">
        💡 <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded font-mono">Entrée</kbd> dans le champ code → recherche · puis dans le champ note → sauvegarde + retour au code
      </div>
    </div>
  );
};

export default SaisieParCodeCopieExamens;
